// LE PANNEAU TECHNIQUE DE L'ÉCRAN DE DIFFUSION — sur les bords de l'image.
//
// Trois pièces, à droite et en bas, à côté du sélecteur de visuels (à
// gauche, tenu par PartyModeController) :
//   PAROLES   trouvées ou non, l'enregistrement LRCLIB de référence (et si sa
//             durée est celle du morceau joué), le RECALAGE appliqué et sa
//             source — radiopi (lignes recalées sur la voix du master) ou le
//             navigateur (corrélation de l'audio de la page), — et d'où vient
//             la position de lecture ;
//   ANALYSE   dansabilité (la valeur du modèle, et ce qui a été retenu), BPM
//             et sa source, statut de l'analyse (cache, analyse, en cours,
//             enregistrement, YouTube introuvable, webradio introuvable…) ;
//   LA LIGNE  la barre de temps du morceau : l'énergie mesurée segment par
//             segment, les changements détectés (marks : drop, montée,
//             descente, mix), et le curseur de lecture — pour juger, à
//             l'instant, où en est le titre et ce que le service a vu.
// `rendre(profile, profil)` repeint les blocs à chaque sondage du profil ;
// `tic(profile)` ne déplace que le curseur, à chaque image.
import { currentPositionSeconds, fmtClock } from './track-debug.js';

const COULEUR_MARK = { drop: '#ff6b6b', phrase: '#c9a6ff', rise: '#f5c451', fall: '#5cc8ff', mix_in: '#ffffff', mix_out: '#ffffff', countdown: '#ff9f1c' };
const NOM_MARK = { drop: 'drop', phrase: 'phrase', rise: 'montée', fall: 'descente', mix_in: 'mix in', mix_out: 'mix out', countdown: 'countdown' };

function att(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
function f2(v) { return v == null || !isFinite(v) ? '—' : Number(v).toFixed(2); }

// Le statut de l'analyse, en un mot, d'après la dernière réponse de
// /api/profil-musical pour ce titre (voir play-boot).
function statutAnalyse(p) {
  if (!p) return { texte: 'en attente…', classe: 'attente' };
  const w = p.webradio && p.webradio.state;
  if (p.status === 'ok') return { texte: p.cache_level ? 'cache' : 'analysé', classe: 'ok', sous: p.route };
  if (p.status === 'processing') {
    if (w === 'recording' || w === 'awaiting_recording' || w === 'uploaded') return { texte: 'enregistrement', classe: 'encours', sous: 'webradio' };
    if (w === 'analyzing') return { texte: 'analyse…', classe: 'encours', sous: 'webradio' };
    return { texte: 'en cours', classe: 'encours', sous: p.route === 'radiopi' ? 'YouTube' : p.route };
  }
  if (p.status === 'unavailable') return { texte: 'sans durée', classe: 'attente', sous: 'estimation seule' };
  if (p.status === 'error') {
    if (/NOT_FOUND/i.test(p.error || '')) return { texte: p.route === 'webradiopi' ? 'webradio NF' : 'YouTube NF', classe: 'echec', sous: null };
    return { texte: 'échec', classe: 'echec', sous: (p.error || '').slice(0, 40) };
  }
  return { texte: p.status || '—', classe: 'attente' };
}

export function creerPanneauDebug(cfg) {
  const elParoles = cfg.parolesEl, elAnalyse = cfg.analyseEl, elLigne = cfg.ligneEl;
  const profils = cfg.profilParTitre || new Map();
  const recalageDe = cfg.recalage || null;
  let recalageVu = '';
  let ligneTrackId = null, ligneDureeS = 0;
  let curseur = null, temps = null, suivant = null, phase = null, marksCourants = [];

  function ligne(cle, valeur, classe) {
    return '<div class="dbg-ligne"><span class="dbg-cle">' + att(cle) + '</span><span class="dbg-val' + (classe ? ' ' + classe : '') + '">' + valeur + '</span></div>';
  }

  function rendreParoles(profile) {
    if (!elParoles) return;
    if (!profile || !profile.title) { elParoles.innerHTML = ''; return; }
    const src = profile.lyricsSource, q = profile.lyricsQuery;
    let etat, classe;
    if (!profile.lyricsResolved) { etat = 'recherche…'; classe = 'attente'; }
    else if (profile.lyricsFound) { etat = 'trouvées'; classe = 'ok'; }
    else { etat = 'introuvables'; classe = 'echec'; }
    let html = '<h4>Paroles</h4>' + ligne('état', att(etat), classe);
    if (src) {
      html += ligne('référence', '<b>' + att(src.trackName || '?') + '</b><br>' + att(src.artistName || '') + (src.albumName ? '<br><i>' + att(src.albumName) + '</i>' : '') + (src.duration != null ? ' · ' + Math.round(src.duration) + ' s' : '') + (src.id != null ? ' · lrc #' + att(src.id) : ''));
    } else if (q) {
      html += ligne('cherché', '« ' + att(q.track_name || q.title || '') + ' »' + (q.artist_name || q.artist ? '<br>' + att(q.artist_name || q.artist) : ''));
    }
    html += lignesRecalage();
    elParoles.innerHTML = html;
    recalageVu = signatureRecalage();
  }

  function signe(v, n) { return v == null || !isFinite(v) ? '—' : (v >= 0 ? '+' : '') + Number(v).toFixed(n == null ? 2 : n); }

  // LE RECALAGE : ce qui est appliqué, par qui, et pourquoi pas quand rien ne
  // l'est. Repeint quand il change (la synchronisation du navigateur bouge
  // toute seule, entre deux sondages du profil).
  function lignesRecalage() {
    if (!recalageDe) return '';
    const r = recalageDe();
    let html = '';
    const src = r.lrclib;
    if (src && src.duration != null && src.playedDuration != null) {
      const ecart = src.duration - src.playedDuration;
      html += ligne('version', 'LRCLIB ' + Math.round(src.duration) + ' s · joué ' + Math.round(src.playedDuration) + ' s', Math.abs(ecart) <= 2 ? 'ok' : 'echec');
    }
    const rp = r.radiopi || {};
    if (rp.source === 'radiopi') {
      html += ligne('recalage', '<b>radiopi</b> · ' + (rp.reference === 'album' ? 'voix de la version de l’album' + (rp.album ? ' « ' + att(rp.album) + ' »' : '') : rp.reference === 'master' ? 'voix du master' : 'voix de la vidéo analysée')
        + '<br>écart médian ' + signe(rp.median) + ' s · dérive ' + signe(rp.derive) + ' s/min · ' + (rp.ancrees || 0) + '/' + (rp.chantees || 0) + ' lignes retrouvées', 'ok');
    } else {
      html += ligne('recalage', 'aucun <span class="dbg-doux">' + att(rp.raison || '') + '</span>', 'attente');
    }
    if (r.reference && r.reference.decalageAnalyse && Math.abs(r.reference.decalageAnalyse.analysis_minus_master_s || 0) >= 0.3) {
      html += ligne('analyse', 'vidéo analysée décalée de ' + signe(r.reference.decalageAnalyse.analysis_minus_master_s, 1) + ' s du master', 'attente');
    }
    if (r.calageTitre) html += ligne('calage titre', signe(r.calageTitre) + ' s <span class="dbg-doux">Video DJ, ce morceau (ou enregistré pour tous)</span>', 'ok');
    if (r.decalageInstallation) html += ligne('installation', signe(r.decalageInstallation) + ' s <span class="dbg-doux">configuration, tous les titres de cette source</span>', 'ok');
    const nv = r.navigateur || {};
    let nav, cl;
    if (nv.etat === 'cale') { nav = '<b>navigateur</b> ' + signe(nv.decalage) + ' s' + (nv.mesure ? ' · confiance ' + f2(nv.mesure.confiance) + ' · pic ' + f2(nv.mesure.ncc) : ''); cl = 'ok'; }
    else if (nv.etat === 'ecoute') { nav = 'écoute… ' + Math.round(nv.fenetre || 0) + '/20 s'; cl = 'encours'; }
    else if (nv.etat === 'incertain') { nav = 'incertain <span class="dbg-doux">' + att(nv.raison || '') + '</span>'; cl = 'attente'; }
    else { nav = (nv.etat || 'inactif') + ' <span class="dbg-doux">' + att(nv.raison || '') + '</span>'; cl = 'attente'; }
    html += ligne('synchro', nav, cl);
    if (r.precision) html += ligne('position', att(r.precision), /seconde/.test(r.precision) ? 'echec' : 'ok');
    return html;
  }

  function signatureRecalage() {
    if (!recalageDe) return '';
    const r = recalageDe(), nv = r.navigateur || {};
    return [r.calageTitre, r.decalageInstallation, r.radiopi && r.radiopi.source, r.radiopi && r.radiopi.raison, nv.etat, nv.raison, nv.decalage && nv.decalage.toFixed(2), Math.round(nv.fenetre || 0), r.precision].join('|');
  }

  function rendreAnalyse(profile) {
    if (!elAnalyse) return;
    if (!profile || !profile.title) { elAnalyse.innerHTML = ''; return; }
    const p = profils.get(profile.trackId) || null;
    const d = profile.danceability, f = profile.trackFeatures;
    const llm = d && d.llmValue != null ? d.llmValue : null;
    const retenu = d && d.value != null ? d.value : null;
    const rejete = !!(d && d.llmOk === false && llm != null);
    // La valeur du modèle d'abord ; puis ce que le service a retenu si ce
    // n'est pas elle (mesure du signal en repli, ou rien : défaut).
    let danse = '<b>' + f2(llm) + '</b> <span class="dbg-doux">llm</span>' + (rejete ? ' <s>écartée</s>' : '');
    if (!d) danse = '—';
    else if (d.source === 'default') danse += ' <span class="dbg-doux">→ défaut 0,50</span>';
    else if (retenu != null && (llm == null || Math.abs(retenu - llm) > 0.005)) danse += ' <span class="dbg-doux">→ ' + f2(retenu) + ' ' + att(d.decidedBy || d.source) + '</span>';
    const bpm = f && f.bpm != null ? '<b>' + Math.round(f.bpm) + '</b> <span class="dbg-doux">' + att(f.bpmSource || '') + (f.bpmConfidence != null ? ' · ' + f2(f.bpmConfidence) : '') + '</span>' : '—';
    const st = statutAnalyse(p);
    elAnalyse.innerHTML = '<h4>Analyse</h4>'
      + ligne('dansabilité', danse)
      + ligne('BPM', bpm)
      + ligne('statut', att(st.texte) + (st.sous ? ' <span class="dbg-doux">' + att(st.sous) + '</span>' : ''), st.classe);
  }

  // LA LIGNE : reconstruite au changement de titre ou d'analyse ; le curseur
  // seul bouge ensuite.
  function rendreLigne(profile) {
    if (!elLigne) return;
    const a = profile && profile.trackAnalysis, tl = profile && profile.analysisTimeline;
    // La ligne couvre le MORCEAU ENTIER. Sur judicael.com, la durée que
    // Soloist annonce est le point de sortie de l'Automix pour ce passage,
    // souvent avant la fin réelle ; l'analyse, elle, porte sur tout le
    // morceau (webradiopi, morceaux.py). On prend la plus longue des deux et
    // la sortie du passage est marquée à part.
    const annoncee = profile && profile.duration > 0 ? profile.duration : 0;
    const duree = Math.max(annoncee, (a && a.duration_s) || 0);
    const sortieS = annoncee > 0 && duree - annoncee > 2 ? annoncee : null;
    const couverture = a && Array.isArray(a.coverage_ms) ? a.coverage_ms : null;
    const cle = (profile && profile.trackId) + '|' + (a ? 'a' : '-') + '|' + Math.round(duree);
    if (!profile || !profile.title) { elLigne.innerHTML = ''; ligneTrackId = null; return; }
    if (cle === ligneTrackId) return;
    ligneTrackId = cle; ligneDureeS = duree;
    const W = 1000, H = 36;
    let svg = '';
    const total = duree * 1000;
    // Les sections de phrases (moteur 1.1.1) avant les segments RMS : c'est
    // ce que l'orchestration lit désormais.
    const phrSec = a && a.structure && a.structure.phrases && Array.isArray(a.structure.phrases.sections) && a.structure.phrases.sections.length ? a.structure.phrases.sections : null;
    const segments = phrSec || ((a && a.structure && Array.isArray(a.structure.segments)) ? a.structure.segments : []);
    if (duree > 0 && segments.length) {
      for (const s of segments) {
        const x = W * s.start_ms / total, w = Math.max(1, W * (s.end_ms - s.start_ms) / total);
        svg += '<rect x="' + x.toFixed(1) + '" y="10" width="' + w.toFixed(1) + '" height="16" class="seg ' + att(s.level) + '"><title>' + att(s.level + ' · ' + fmtClock(s.start_ms) + ' → ' + fmtClock(s.end_ms) + (s.energy != null ? ' · énergie ' + s.energy : '') + ' · ' + s.avg_db + ' dB') + '</title></rect>';
      }
      // l'énergie continue (avg_db des segments remis sur 0-100), en courbe
      if (tl && typeof tl.lookupEnergy === 'function') {
        const pts = [];
        for (let i = 0; i <= 200; i++) { const ms = total * i / 200; const e = tl.lookupEnergy(ms); pts.push((W * i / 200).toFixed(1) + ',' + (26 - 16 * (e == null ? 0 : e / 100)).toFixed(1)); }
        svg += '<polyline points="' + pts.join(' ') + '" class="energie"/>';
      }
    } else if (duree > 0) {
      svg += '<rect x="0" y="10" width="' + W + '" height="16" class="seg vide"/>';
    }
    marksCourants = (duree > 0 && tl) ? tl.marks : [];
    if (duree > 0) {
      // en pied : les CANDIDATS du service — les frontières de phrase avec
      // leur nouveauté (moteur 1.1.0), sinon les anciens drops. Hauteur et
      // opacité suivent la nouveauté : on voit d'un coup d'œil ce que le
      // seuil a retenu et ce qu'il a laissé.
      const phr = a && a.structure && a.structure.phrases;
      if (phr && Array.isArray(phr.candidates)) {
        for (const c of phr.candidates) {
          const h = Math.min(8, 2 + 2 * (c.novelty || 0));
          svg += '<line x1="' + (W * c.time_ms / total).toFixed(1) + '" x2="' + (W * c.time_ms / total).toFixed(1) + '" y1="26" y2="' + (26 + h).toFixed(1) + '" class="cand" style="opacity:' + Math.min(1, 0.2 + 0.25 * (c.novelty || 0)).toFixed(2) + '"><title>' + att('phrase ' + fmtClock(c.time_ms) + ' · nouveauté ' + (c.novelty || 0).toFixed(2)) + '</title></line>';
        }
      }
      // LE SIGNAL « DROP » DU SERVICE, en tête de ligne : les candidats de
      // _compute_drops (dérivée du niveau RMS × force d'attaque, pics à 3 s
      // d'écart au moins), hauteur et opacité par confiance. Ils ne pilotent
      // plus l'image quand les phrases existent ; ils sont là pour comparer.
      const drops = (a && a.structure && a.structure.cue_points && a.structure.cue_points.drops) || [];
      for (const dr of drops) {
        const x = (W * dr.time_ms / total).toFixed(1), conf = dr.confidence || 0;
        svg += '<line x1="' + x + '" x2="' + x + '" y1="1" y2="' + (1 + 3 + 6 * conf).toFixed(1) + '" class="drop" style="opacity:' + (0.25 + 0.7 * conf).toFixed(2) + '"><title>' + att('drop (service) ' + fmtClock(dr.time_ms) + ' · confiance ' + conf.toFixed(2)) + '</title></line>';
      }
      for (const m of marksCourants) {
        const x = (W * m.atMs / total).toFixed(1);
        svg += '<line x1="' + x + '" x2="' + x + '" y1="10" y2="26" stroke="' + (COULEUR_MARK[m.type] || '#fff') + '" stroke-width="' + (m.type === 'drop' && m.score >= 0.85 ? 3 : 1.5) + '"><title>' + att((NOM_MARK[m.type] || m.type) + ' ' + fmtClock(m.atMs) + ' · ' + m.score.toFixed(2)) + '</title></line>';
      }
    }
    // Ce qui n'a jamais été entendu du morceau (silence comblé) : grisé.
    if (duree > 0 && couverture && couverture.length) {
      let t = 0;
      for (const c of couverture.concat([[total, total]])) {
        if (c[0] - t > 500) svg += '<rect x="' + (W * t / total).toFixed(1) + '" y="10" width="' + (W * (c[0] - t) / total).toFixed(1) + '" height="16" class="non-entendu"><title>' + att('jamais entendu ' + fmtClock(t) + ' → ' + fmtClock(c[0])) + '</title></rect>';
        t = Math.max(t, c[1]);
      }
    }
    // La sortie de l'Automix pour ce passage.
    if (sortieS != null) {
      const xs = (W * sortieS * 1000 / total).toFixed(1);
      svg += '<line x1="' + xs + '" x2="' + xs + '" y1="4" y2="32" class="sortie"><title>' + att('sortie de l’Automix ' + fmtClock(sortieS * 1000) + ' · morceau ' + fmtClock(total)) + '</title></line>';
    }
    svg += '<line id="dbg-curseur" x1="0" x2="0" y1="0" y2="' + H + '" class="curseur"/>';
    elLigne.innerHTML = '<div class="dbg-ligne-tete"><span id="dbg-temps">—</span><span id="dbg-phase"></span><span id="dbg-suivant"></span><span class="dbg-doux dbg-legende"><i style="color:#ff6b6b">▏</i>drop service <i style="color:#c9a6ff">▏</i>phrase <i style="color:#ff6b6b">▍</i>mark drop</span><span class="dbg-doux">' + (duree > 0 ? (sortieS != null ? 'sortie ' + fmtClock(sortieS * 1000) + ' / ' : '') + fmtClock(duree * 1000) : 'flux') + '</span></div>'
      + '<svg viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none" class="dbg-svg">' + svg + '</svg>'
      + (marksCourants.length || segments.length ? '' : '<div class="dbg-doux dbg-sans">' + (duree > 0 ? 'pas d’analyse pour ce titre' : 'flux sans durée : pas de ligne') + '</div>');
    curseur = elLigne.querySelector('#dbg-curseur'); temps = elLigne.querySelector('#dbg-temps'); phase = elLigne.querySelector('#dbg-phase'); suivant = elLigne.querySelector('#dbg-suivant');
  }

  let dernierTic = 0;
  function tic(profile) {
    if (!elLigne || !curseur || !profile || !profile.title) return;
    const pos = currentPositionSeconds(profile);
    if (ligneDureeS > 0) { const x = Math.max(0, Math.min(1000, 1000 * pos / ligneDureeS)); curseur.setAttribute('x1', x); curseur.setAttribute('x2', x); }
    const now = performance.now();
    if (now - dernierTic < 250) return;
    dernierTic = now;
    if (recalageDe && signatureRecalage() !== recalageVu) rendreParoles(profile);
    if (temps) temps.textContent = fmtClock(pos * 1000) + (typeof window !== 'undefined' && Number.isFinite(window.__latenceRadio) ? ' · latence ' + window.__latenceRadio.toFixed(1) + ' s' : '');
    const tl = profile.analysisTimeline;
    if (phase) phase.textContent = tl ? (tl.lookupMode(pos * 1000) || '') : '';
    if (suivant) {
      const m = tl ? tl.marks.find((x) => x.atMs > pos * 1000) : null;
      suivant.textContent = m ? (NOM_MARK[m.type] || m.type) + ' dans ' + Math.max(0, Math.round((m.atMs - pos * 1000) / 1000)) + ' s' : '';
      suivant.style.color = m ? COULEUR_MARK[m.type] : '';
    }
  }

  return {
    rendre: function (profile) { rendreParoles(profile); rendreAnalyse(profile); rendreLigne(profile); },
    tic: tic
  };
}
